import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Grid,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
} from "@material-ui/core";
import PaginateTable from "components/helpers/PaginateTable";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { useHistory } from "react-router-dom";
import TextTypo from "components/helpers/TextTypo";
import { loanApplicationAction } from "redux/actions/applicationAction";
import { getAllBanksAction } from "redux/actions/bankActions";
import SimpleTabs from "components/helpers/SimpleTabs";
import Table from "components/Table";
import Radio from "components/Radio";
import SearchInput from "components/Input/Search";
import Button from "components/Button";
import { RemoveRedEye } from "@material-ui/icons";
import { Tag } from "antd";
import {
  getStatusColor,
  applicantFilters,
  decodeToken,
  exportFromJson,
} from "utils";
import moment from "moment";
import Filter from "components/Filter";
import { BankOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { loanApplicationService } from "services/applicationService";
import { calculateTenorInMonths } from "views/Banks/Home";


const useStyles = makeStyles({
  root: {
    width: "100%",
  },
});


const ViewApplication = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();


  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [applicationId, setApplicationId] = useState();
  const [searchOption, setSearchOption] = useState("names");
  const [searchValue, setSearchValue] = useState(null);
  const [filterBy, setFilterBy] = useState({});
  const [customFilters, setCustomFilter] = useState(applicantFilters);
  const [isExporting, setIsExporting] = useState(false);


  const [anchorEl, setAnchorEl] = useState(null);
  const { loanApplications, applicationLoading, allBanks } = useSelector(
    ({ loanApplications, backendUsers }) => ({
      loanApplications: loanApplications?.data,
      applicationLoading: loanApplications?.loading,
      allBanks: backendUsers?.allBanks?.data,
    })
  );


  const level =
    decodeToken()?.userRole?.RoleName === "Bank Officer" ? "bank" : "backend";


    const queryData = useCallback(
      (newPage = page, data = filterBy) => {
        const payload = {
          page: newPage,
          limit: rowsPerPage,
          level: level === "bank" ? "bank" : undefined,
          ...data,
        };
        dispatch(loanApplicationAction(payload));
      },
      [dispatch, rowsPerPage, level, filterBy]
    );


  const role = decodeToken().userRole?.RoleName;
  const backendUser = role !== "Super Admin" && role !== "Admin";


  const columns = [
    {
      id: "Names",
      dataIndex: "applicant",
      title: `${t("names")}`,
      render: ({ firstName, lastName }) => firstName + " " + lastName,
    },
    {
      id: "nationalId",
      dataIndex: "applicant",
      title: `${t("id-number")}`,
      render: ({ nationalId }) => nationalId,
    },
    {
      id: "phone",
      dataIndex: "propertyValueLimit",
      title: `${t("property-value")}`,
      render: (propertyValueLimit) =>
        "RWF " + propertyValueLimit?.toLocaleString(),
    },
    {
      id: "referenceId",
      dataIndex: "referenceId",
      title: `${t("reference-id")}`,
    },
    {
      id: "views",
      dataIndex: "views",
      title: `${t("views")}`,
      render: (views) => views?.total?.toLocaleString() || 0,
    },
    {
      id: "level",
      dataIndex: "level",
      title: `${t("level")}`,
      render: (level) => (
        <Tooltip title={level === "bank" ? "Bank" : "Backend-Officer"}>
          {level === "bank" ? <p>Bank</p> : <p>Backend-Officer</p>}
        </Tooltip>
      ),
    },
    // {
    //   id: 'lastView',
    //   dataIndex: 'lastView',
    //   title: `Last Viewed`,
    //   render: (lastView) =>
    //     lastView?.lastViewDate
    //       ? moment(lastView?.lastViewDate).from()
    //       : 'Never',
    // },
    {
      id: "action",
      dataIndex: "id",
      title: `${t("action")}`,
      render: (id, record) => {
        return (
          <Button
            onClick={() =>
              history.push(
                backendUser ? `/loan/applications/${id}` : `/applications/${id}`
              )
            }
            icon={<RemoveRedEye />}
          />
        );
      },
    },
  ];


  const handleCloseMenu = () => {
    setAnchorEl(null);
  };


  const viewApplication = () => {
    history.push(`/applications/${applicationId}`);
  };


  const handleSearch = (value) => {
    let payload = {};
    switch (searchOption) {
      case "referenceId":
        payload.referenceId = value;
        break;


      case "nationalId":
        payload.nationalId = value;
        break;


      case "names":
        payload.names = value;
        break;


      default:
        payload = {};
    }
    dispatch(loanApplicationAction(payload));
  };


  const onSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    if (!value) {
      dispatch(loanApplicationAction());
    }
  };


  useEffect(() => {
    dispatch(getAllBanksAction());
  }, []);


  useEffect(() => {
    if (allBanks?.Banks) {
      const bankFilters = allBanks?.Banks?.map((bank) => ({
        id: bank.id,
        value: `${bank.id}`,
        name: bank.name,
      }));


      const isLoanProviderPresent = customFilters.find(
        (filter) => filter.id === "processingBankId"
      );


      if (!isLoanProviderPresent) {
        setCustomFilter([
          ...customFilters,
          {
            id: "processingBankId",
            title: "Loan Provider",
            value: "processingBankId",
            filters: bankFilters,
          },
        ]);
      }
    }
  }, [allBanks]);


  useEffect(() => {
    const fetchData = () => {
      queryData(undefined, filterBy);
    };
    fetchData();
  }, [filterBy]);


  // const handleExport = async () => {
  //   setIsExporting(true);
  //   let payload = { ...filterBy, limit: 99999999 };
  //   const {
  //     data: { data },
  //   } = (await loanApplicationService(payload, 'export')) || {};
  //   const fileName = `Giriwawe_applicants`;
  //   const dataToExport = data?.loanApplications?.rows?.map((applicant) => ({
  //     Reference: applicant?.referenceId,
  //     'First Name': applicant?.applicant?.firstName,
  //     'Last Name': applicant?.applicant?.lastName,
  //     'Nation ID': applicant?.applicant?.nationalId,
  //     Gender: applicant?.applicant?.gender,
  //     Phone: applicant?.applicant?.phone,
  //     'Marital Status': applicant?.applicant?.maritalStatus,
  //     'RRA Employer': applicant?.applicant?.rraEmployerName,
  //     Profession: applicant?.applicant?.profession,
  //     'Net Salary': applicant?.applicant?.salarynet,
  //     'RRA Net Salary': applicant?.applicant?.rraNetIncome,
  //     'Household Combined Income': applicant?.rraCombinedHouseHoldIncome,
  //     'RRA Gross Income': applicant?.applicant?.rraGrossIncome,
  //     'Bank Provideer (1st)': applicant?.bankProvider1?.name,
  //     'House Category (1st)': applicant?.houseCategory1?.name,
  //     'Present Value': applicant?.presentValue,
  //     'Property Value Limit': applicant?.propertyValueLimit,
  //     'Approved Loan Amount': applicant?.approvedLoanAmount,
  //     Views: applicant?.views?.total,
  //     'Backend Status': applicant?.backEndStatus,
  //     'Bank Status': applicant?.bankStatus,
  //     Period: applicant?.years,
  //   }));
  //   exportFromJson(dataToExport, fileName);
  //   setIsExporting(false);
  // };
  const handleExport = async () => {
    setIsExporting(true);


    let payload = { ...filterBy, limit: 99999999 };


    switch (searchOption) {
      case "referenceId":
        if (searchValue) {
          payload.referenceId = searchValue;
        }
        break;


      case "nationalId":
        if (searchValue) {
          payload.nationalId = searchValue;
        }
        break;


      case "names":
        if (searchValue) {
          payload.names = searchValue;
        }
        break;


      default:
        payload;
    }


    const {
      data: { data },
    } = (await loanApplicationService(payload, "export")) || {};


    const dataToExport = data?.loanApplications?.rows?.map((applicant) => {
      const bankName = applicant?.processingBankId
        ? allBanks?.Banks?.find(
            (bank) => bank.id === applicant?.processingBankId
          )?.name
        : "";
      const loanToValueRatio =
        (applicant?.approvedLoanAmount / applicant?.housePurchaseAmount) * 100;


      return {
        Reference: applicant?.referenceId,
        "Borrower Name": `${applicant?.applicant?.firstName} ${applicant?.applicant?.lastName}`,
        "Borrower NID": applicant?.applicant?.nationalId,
        Phone: applicant?.applicant?.phone,
        "Loan Account": applicant?.loanAccountNumber || "",
        "Monthly Household Income": applicant?.applicant?.salarynet,
        "House Cost": applicant?.housePurchaseAmount,
        "Disbursed Loan Amount": applicant?.approvedLoanAmount,
        "Bank Name": bankName,
        "Loan To Value Ratio": !isNaN(loanToValueRatio) ? loanToValueRatio : 0,
        "Loan Full Disbursed":
          applicant?.bankStatus === "Disbursed" ? "Yes" : "No",
        "Monthly Installments": applicant?.monthlyInstallment,
        "Loan Start Date": applicant?.disbursedDate,
        "Loan Maturity Date": applicant?.maturityDate,
        "Tenor in months": calculateTenorInMonths(
          applicant?.disbursedDate,
          applicant?.maturityDate
        ),
        "Interest Rate": `${String(applicant?.interestRate * 100)}%`,
        "UPI Number": applicant?.houseUpi,
        "Status for RDB Perfection": applicant?.perfectionStatus,
        "Perfection Termination Date": applicant?.perfectionTerminationDate,
        "Fire Insurance Expiry Date": applicant?.fireInsuranceExpiryDate,
        Village: applicant?.village,
        Cell: applicant?.cell,
        Sector: applicant?.sector,
        District: applicant?.district,
      };
    });


    // Export only the displayed data
    exportFromJson(dataToExport, "Giriwawe_applicants");


    setIsExporting(false);
  };


  return (
    <div style={{ marginTop: 20 }}>
      <Container>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <TextTypo
              text={`${t("application")} (${
                loanApplications?.loanApplications?.count
              })`}
              gutterBottom={true}
              variant="h4"
            />
          </Grid>
          <Grid item>
            <div className="card my-2">
              <div className="d-flex justify-content-around">
                <div className="card my-3 p-3 w-25 ">
                  <h6 className="text-center" style={{ fontWeight: "bold" }}>
                    {t("level")}
                  </h6>
                  <div className="d-flex justify-content-between">
                    {loanApplications?.aggregation?.levelSummary?.map(
                      (summary, key) => (
                        <div key={key}>
                          <Tag className="text-capitalize">{summary.level}</Tag>
                          <h3 className="text-center p-3">
                            {summary.levelCount}
                          </h3>
                        </div>
                      )
                    )}
                  </div>
                </div>


                <div className="card my-3 p-3">
                  <h6
                    className="text-center text-bold"
                    style={{ fontWeight: "bold" }}
                  >
                    RHA/Backend
                  </h6>
                  <div className="d-flex justify-content-between">
                    {loanApplications?.aggregation?.backEndStatusSummary?.map(
                      (summary, key) => (
                        <div key={key}>
                          <Tag color={getStatusColor(summary.backEndStatus)}>
                            {summary.backEndStatus}
                          </Tag>
                          <h3 className="text-center p-3">
                            {summary.backEndStatusCount}
                          </h3>
                        </div>
                      )
                    )}
                  </div>
                </div>


                <div className="card my-3 p-3 w-25">
                  <h6 className="text-center" style={{ fontWeight: "bold" }}>
                    {t("bank")}
                  </h6>
                  <div className="d-flex justify-content-between">
                    {loanApplications?.aggregation?.bankStatusSummary?.map(
                      (summary, key) => (
                        <div key={key}>
                          <Tag color={getStatusColor(summary.bankStatus)}>
                            {summary.bankStatus}
                          </Tag>
                          <h3 className="text-center p-3">
                            {summary.bankStatusCount}
                          </h3>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div item xs={6} sm={6} md={6}>
                <Radio
                  options={[
                    { label: `${t("names")}`, value: "names" },
                    { label: `${t("id-number")}`, value: "nationalId" },
                    { label: `${t("reference-id")}`, value: "referenceId" },
                  ]}
                  value={searchOption}
                  onChange={(e) => setSearchOption(e.target.value)}
                />
                <SearchInput
                  placeholder={
                    searchOption === "nationalId"
                      ? "Ex: 1192770003989043"
                      : searchOption === "referenceId"
                      ? "Ex: W42Q5LV0LY"
                      : "Ex: Nyirambonabushya"
                  }
                  onSearch={handleSearch}
                  onChange={onSearchChange}
                  loading={applicationLoading}
                  className="my-3 w-75"
                  btnText={`${t("search")}`}
                />
              </div>
              <div item xs={6} sm={6} md={6} className="mt-5 pt-4">
                <Button type="primary" onClick={handleExport}>
                  {t("export")}
                </Button>
              </div>
            </div>
          </Grid>
          <Grid item className={classes.root}>
            <Grid container direction="row">
              <Grid xs={3}>
                <Filter
                  filterOptions={customFilters}
                  filterBy={filterBy}
                  setFilterBy={setFilterBy}
                />
              </Grid>
              <Grid xs={9}>
                <Table
                  columns={columns}
                  data={loanApplications?.loanApplications?.rows}
                  total={loanApplications?.totalCount}
                  loading={applicationLoading}
                  onPageChange={(page, limit) => {
                    setPage(page - 1);
                    setRowsPerPage(limit);
                
                    let payload = {
                      page: page - 1,
                      limit,
                      ...filterBy, // include current filters
                    };
                    dispatch(loanApplicationAction(payload));
                  }}
                /> 


                <Menu
                  id="simple-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                >
                  <MenuItem onClick={viewApplication}>{t("view")}</MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};


export default ViewApplication;



